// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { color } from "@bg/XDL/Utilities";
import { layoutContainer } from "@bg/XDL/Utilities/sharedStyles";
import xScreen from "@bg/XDL/Utilities/xScreen";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import Button, { ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button/Button";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { css } from "styled-components";

interface SaveQtyStickyStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    hiddenWrapper?: InjectableCss;
    button?: ButtonPresentationProps;
}

interface Size {
    bottom?: number;
    height?: number;
    left?: number;
    right?: number;
    top?: number;
    width?: number;
    x?: number;
    y?: number;
}

const saveQtyStickyStyles: SaveQtyStickyStyles = {
    container: {
        css: css`
            position: sticky;
            bottom: 0;
            z-index: 9;
            width: 100vw;

            &.sticky {
                box-shadow: 0 0 25px 0 rgba(79, 79, 119, 0.18);
                background: ${color("common.white")};
            }

            @media print {
                display: none;
            }

            ${({ alignX }: any) =>
                alignX === -1
                    ? css`
                          visibility: hidden;
                      `
                    : css`
                          transform: translateX(-${alignX}px);
                      `}
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            padding-top: 16px;
            padding-bottom: 16px;

            ${layoutContainer}
        `,
    },
    hiddenWrapper: {
        css: css`
            visibility: hidden;
        `,
    },
    button: {
        css: css`
            transition: all 200ms ease-in-out;

            &.sticky {
                margin-right: 2.5%;
            }
            ${xScreen("lte", "xs")} {
                &.sticky {
                    width: 100%;
                    margin-right: 0;
                }
            }
        `,
    },
};

const styles = saveQtyStickyStyles;
const SaveQtySticky: FC<ButtonProps> = ({ className, css, ...props }) => {
    const [size, setSize] = useState<Size>({});
    const [isSticky, setIsSticky] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const updateSize = useCallback(() => {
        if (wrapperRef.current) {
            setSize(wrapperRef.current.getBoundingClientRect());
        }
    }, []);

    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            setIsSticky(!entry.isIntersecting);
        });
    });

    useEffect(() => {
        updateSize();
        window.addEventListener("resize", updateSize);

        if (!wrapperRef.current) {
            return;
        }

        // Observe the sticky element
        observer.observe(wrapperRef.current);

        return () => {
            window.removeEventListener("resize", updateSize);
            observer.disconnect();
        };
    }, []);

    return (
        <>
            <StyledWrapper
                {...styles.container}
                className={`sticky-element ${isSticky ? "sticky" : ""}`}
                {...{ alignX: size.x ?? -1 }}
            >
                <StyledWrapper {...styles.wrapper}>
                    <Button
                        {...props}
                        css={[styles.button?.css, css]}
                        className={`${isSticky ? "sticky" : ""} ${className || ""}`.trim()}
                    >
                        {siteMessage("Custom_Update_Products_Qty")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper ref={wrapperRef}></StyledWrapper>
        </>
    );
};

export default SaveQtySticky;
